import {Group} from '@visx/group';
import {XYChart} from '@visx/xychart';
import React, {ReactNode, useMemo} from 'react';

import {AxisOrientation} from 'ui-library/charting/internals/Axis';
import fitChartAndLabels from 'ui-library/charting/internals/utils/fitChartAndLabels';
import getTickLabelWidths from 'ui-library/charting/internals/utils/getTickLabelWidths';
import {VisxAxisConfig} from 'ui-library/charting/internals/utils/getVisxAxisConfig';
import {DEFAULT_CARTESIAN_CHART_MARGINS, POINT_RADIUS} from 'ui-library/charting/internals/utils/Theme';
import {Margin} from 'ui-library/charting/internals/VisxUtilityTypes';

import ChartGrid from '../ChartGrid';
import {XAxis, YAxis} from './CartesianAxis';
import {HOVER_RADIUS_MULTIPLIER} from './CartesianPoint';
import ChartMask from './ChartMask';
import computeCartesianTheme from './computeCartesianTheme';

export const DEFAULT_XYCHART_PROPS = {
  // Set to false so child components can trigger their own event handlers
  captureEvents: false,
};

export const MASK_MARGIN = 1.5 * HOVER_RADIUS_MULTIPLIER * POINT_RADIUS;
export interface CartesianPlotProps {
  children: ReactNode;
  height: number;
  width: number;
  xAxis: VisxAxisConfig;
  yAxis: VisxAxisConfig;
  extraMargin?: Margin;
}

export default function CartesianPlot(props: CartesianPlotProps): JSX.Element {
  const {children, height, width, xAxis: initialXAxisConfig, yAxis: initialYAxisConfig, extraMargin} = props;

  const {chartWidth, chartHeight, longestYTickLabelWidth, margins, xAxisConfig, yAxisConfig} = useMemo(() => {
    const {chartWidth, chartHeight, margins, xAxisConfig, yAxisConfig} = fitChartAndLabels({
      defaultMargins: DEFAULT_CARTESIAN_CHART_MARGINS,
      height,
      width,
      xAxisOrientation: AxisOrientation.BOTTOM,
      xAxisConfig: initialXAxisConfig,
      yAxisConfig: initialYAxisConfig,
      extraMargin,
    });

    return {
      chartWidth,
      chartHeight,
      longestYTickLabelWidth: Math.max(...getTickLabelWidths(yAxisConfig)),
      margins,
      xAxisConfig,
      yAxisConfig,
    };
  }, [height, width, initialXAxisConfig, initialYAxisConfig, extraMargin]);

  // XYChart performs this range setting operation automatically by calculating it's own version of `chart[Width/Height]`,
  // but since we want to use the scales outside of the XYChart context with `ChartGrid`, we need to perform this manually.
  xAxisConfig.scale.range([0, chartWidth]);
  yAxisConfig.scale.range([chartHeight, 0]);
  return (
    <>
      {/* ChartGrid is implemented using Visx's base Grid component instead of XYChart's Grid component, so it needs
      to be rendered outside <XYChart> with a surrounding Group that gives it similar positioning. */}
      <Group left={margins.left} top={margins.top}>
        <ChartGrid
          xAxisConfig={xAxisConfig}
          yAxisConfig={yAxisConfig}
          width={chartWidth}
          height={chartHeight}
        />
      </Group>
      <XYChart
        {...DEFAULT_XYCHART_PROPS}
        height={height}
        margin={margins}
        width={width}
        theme={computeCartesianTheme(
          xAxisConfig.tickLabelRotation !== 0,
          margins.bottom,
          longestYTickLabelWidth
        )}
        xScale={xAxisConfig}
        yScale={yAxisConfig}
      >
        <ChartMask
          // Make the mask's margins a bit bigger, so that we don't cut off points that are draw on the edge or axes.
          margins={{
            bottom: margins.bottom - MASK_MARGIN,
            left: margins.left - MASK_MARGIN,
            right: margins.right - MASK_MARGIN,
            top: margins.top - MASK_MARGIN,
          }}
          width={width}
          height={height}
        />
        <XAxis
          label={xAxisConfig.label}
          tickLabelRotation={xAxisConfig.tickLabelRotation ?? 0}
          suggestedNumTicks={xAxisConfig.suggestedNumTicks}
          tickLabelFormatter={xAxisConfig.tickLabelFormatter}
        />
        <YAxis
          label={yAxisConfig.label}
          suggestedNumTicks={yAxisConfig.suggestedNumTicks}
          tickLabelFormatter={yAxisConfig.tickLabelFormatter}
        />
        {children}
      </XYChart>
    </>
  );
}
